import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import api from '../utils/api';
import { X, Loader, CheckCircle2 } from 'lucide-react';

// 后台代操作弹层（管理员/代理共用）
// mode='complete'（默认）：一键完成——把该用户所有活跃批次直接置为 completed，
//   进入等待结算流程。不再要求逐批次传图（图片由独立的"代上传"负责）。
// mode='upload'：代上传——一次性多选图片，全部上传到该用户名下（不按批次），
//   供代理商随时补充工作凭证。图片记录归属目标用户（targetUserId）。
export default function ForceCompleteModal({ userId, userName, mode = 'complete', onClose, onDone }) {
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const fileRef = React.useRef(null);

  const isUploadOnly = mode === 'upload';

  // ── 一键完成 ──
  const handleComplete = async () => {
    setSubmitting(true);
    setError('');
    try {
      const res = await api.post(`/dispatch/force-complete/${userId}`);
      setDone(true);
      if (onDone) onDone(res.data);
      setTimeout(() => { onClose(); }, 1200);
    } catch (err) {
      setError(err.response?.data?.error || '代完成失败');
    } finally {
      setSubmitting(false);
    }
  };

  // ── 代上传：一次性多选，全部传到该用户名下 ──
  const pickFiles = () => { if (fileRef.current) fileRef.current.click(); };
  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    setError('');
    try {
      const fd = new FormData();
      for (const f of files) fd.append('images', f);
      // 一次性上传到该用户名下（图片归属 targetUser，不再按批次）
      await api.post(`/images/upload/any?targetUserId=${userId}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setDone(true);
      if (onDone) onDone({ count: files.length });
      setTimeout(() => { onClose(); }, 1200);
    } catch (err) {
      setError(err.response?.data?.error || '上传失败');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return createPortal(
    <div style={{
      position: 'fixed', inset: 0, zIndex: 2147483600,
      background: 'rgba(0,0,0,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20
    }} onClick={(submitting || uploading || done) ? undefined : onClose}>
      <div style={{
        background: 'var(--c-surface)', borderRadius: 20, width: '100%', maxWidth: 420,
        padding: '18px 16px', boxShadow: '0 12px 40px rgba(0,0,0,0.3)'
      }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <span style={{ fontSize: 16, fontWeight: 700 }}>{isUploadOnly ? '代上传' : '代完成'} - {userName}</span>
          <button onClick={onClose} disabled={submitting || uploading || done}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
            <X size={18} color="var(--c-text-secondary)" />
          </button>
        </div>

        {done ? (
          <div style={{ textAlign: 'center', padding: '16px 0 8px' }}>
            <CheckCircle2 size={48} color="var(--c-success)" style={{ margin: '0 auto 10px', display: 'block' }} />
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--c-success)' }}>
              {isUploadOnly ? '图片已上传到该用户名下' : '已代为完成，进入等待结算'}
            </div>
          </div>
        ) : isUploadOnly ? (
          <>
            <div style={{ fontSize: 13, color: 'var(--c-text-secondary)', lineHeight: 1.7, marginBottom: 12 }}>
              一次性选择多张工作凭证图片，将全部上传到「{userName}」名下（可多次上传补充）。
            </div>
            {error && <p style={{ color: 'var(--c-danger)', fontSize: 12, marginBottom: 8 }}>{error}</p>}
            <input ref={fileRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleFiles} />
            <button className="btn btn-primary" onClick={pickFiles} disabled={uploading} style={{ width: '100%' }}>
              {uploading ? <><Loader size={14} className="spin" style={{ verticalAlign: '-2px', marginRight: 4 }} />上传中...</> : '选择图片上传'}
            </button>
          </>
        ) : (
          <>
            <div style={{ fontSize: 13, color: 'var(--c-text-secondary)', lineHeight: 1.7, marginBottom: 12 }}>
              将「{userName}」名下全部进行中的批次标记为已完成，进入等待结算流程。
              工作凭证图片可在「代上传」或「图片管理」中随时补充。
            </div>
            {error && <p style={{ color: 'var(--c-danger)', fontSize: 12, marginBottom: 8 }}>{error}</p>}
            <button className="btn btn-primary" onClick={handleComplete} disabled={submitting} style={{ width: '100%' }}>
              {submitting ? <><Loader size={14} className="spin" style={{ verticalAlign: '-2px', marginRight: 4 }} />完成中...</> : '确认代完成'}
            </button>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}